import React, { useState } from "react";
import ClaimProgress from "./ClaimProgress";
import { Clock, Inbox, Search, BadgeCheck, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const steps = [
  { 
    key: "pending", 
    label: "Pending", 
    description: "Your claim is being submitted",
    Icon: Clock,
    color: "amber"
  },
  { 
    key: "received", 
    label: "Received", 
    description: "We've received your claim",
    Icon: Inbox,
    color: "blue"
  },
  { 
    key: "reviewing", 
    label: "Reviewing", 
    description: "Your claim is under review",
    Icon: Search,
    color: "indigo"
  },
  { 
    key: "claimed", 
    label: "Claimed", 
    description: "Your claim has been approved",
    Icon: BadgeCheck,
    color: "emerald"
  },
];

const getIndexFromStatus = (status) => {
  const idx = steps.findIndex((s) => s.key === status);
  return idx === -1 ? 0 : idx;
};

const colorConfigs = {
  amber: {
    gradient: "from-amber-400 to-orange-500",
    light: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
  },
  blue: {
    gradient: "from-blue-400 to-cyan-500",
    light: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconBg: "bg-blue-100",
  },
  indigo: {
    gradient: "from-indigo-400 to-purple-500",
    light: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-700",
    iconBg: "bg-indigo-100",
  },
  emerald: {
    gradient: "from-emerald-400 to-green-500",
    light: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
  },
  gray: {
    gradient: "from-gray-300 to-gray-400",
    light: "bg-gray-50",
    border: "border-gray-200",
    text: "text-gray-500",
    iconBg: "bg-gray-100",
  },
};

const ClaimProgressResponsive = ({ status = "pending", variant = "emerald" }) => {
  const [open, setOpen] = useState(false);
  const current = getIndexFromStatus(status);

  return (
    <div>
      {/* Desktop / Tablet: Modern Horizontal Tracker */}
      <div className="hidden sm:block">
        <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Claim Status</h3>
            <p className="text-xs text-gray-500">Track your claim progress</p>
          </div>
          <ClaimProgress status={status} variant={variant} />
        </div>
      </div>

      {/* Mobile: Enhanced Vertical Tracker */}
      <div className="sm:hidden">
        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          whileTap={{ scale: 0.98 }}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${
              current === 0 ? colorConfigs.amber.gradient :
              current === 1 ? colorConfigs.blue.gradient :
              current === 2 ? colorConfigs.indigo.gradient :
              colorConfigs.emerald.gradient
            }`}>
              {current === 0 ? <Clock size={18} className="text-white" /> :
               current === 1 ? <Inbox size={18} className="text-white" /> :
               current === 2 ? <Search size={18} className="text-white" /> :
               <BadgeCheck size={18} className="text-white" />}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">
                {steps[current]?.label || "Pending"}
              </p>
              <p className="text-xs text-gray-500">
                {steps[current]?.description || "Track your claim"}
              </p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={20} className="text-gray-400" />
          </motion.div>
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-3 overflow-hidden"
            >
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="relative pl-8">
                  {/* Vertical Progress Line */}
                  <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200 rounded-full" />
                  
                  {/* Active Progress Line */}
                  <motion.div
                    className={`absolute left-3 top-0 w-0.5 bg-gradient-to-b ${
                      current === 0 ? "from-amber-400" :
                      current === 1 ? "from-amber-400 via-blue-400" :
                      current === 2 ? "from-amber-400 via-blue-400 via-indigo-400" :
                      "from-amber-400 via-blue-400 via-indigo-400 to-emerald-400"
                    } rounded-full`}
                    initial={{ height: 0 }}
                    animate={{ 
                      height: current === 0 ? "25%" : 
                               current === 1 ? "50%" : 
                               current === 2 ? "75%" : "100%"
                    }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                  />

                  <div className="space-y-6">
                    {steps.map((step, idx) => {
                      const isDone = idx < current;
                      const isActive = idx === current;
                      const isPending = idx > current;
                      
                      const config = isPending 
                        ? colorConfigs.gray 
                        : isActive 
                        ? colorConfigs[step.color] 
                        : colorConfigs[step.color];

                      const Icon = step.Icon;

                      return (
                        <motion.div
                          key={step.key}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="relative flex gap-4 items-start"
                        >
                          {/* Step Node */}
                          <div className={`relative flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                            isActive
                              ? `bg-gradient-to-br ${config.gradient} shadow-lg`
                              : isDone
                              ? `bg-gradient-to-br ${config.gradient} shadow-md`
                              : "bg-gray-100 border-2 border-gray-300"
                          } transition-all duration-300`}>
                            {isDone ? (
                              <CheckCircle2 size={14} className="text-white" />
                            ) : (
                              <Icon 
                                size={14} 
                                className={isActive || isDone ? "text-white" : "text-gray-400"} 
                              />
                            )}
                            
                            {/* Pulsing Ring for Active Step */}
                            {isActive && (
                              <>
                                <motion.div
                                  className={`absolute inset-0 rounded-full bg-gradient-to-br ${config.gradient}`}
                                  animate={{
                                    scale: [1, 1.8, 1.8],
                                    opacity: [0.4, 0, 0],
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeOut",
                                  }}
                                />
                              </>
                            )}
                          </div>

                          {/* Step Content */}
                          <div className="flex-1 pt-0.5">
                            <div className={`p-3 rounded-lg ${
                              isActive 
                                ? `${config.light} ${config.border} border` 
                                : "bg-gray-50"
                            } transition-all duration-300`}>
                              <div className="flex items-center justify-between mb-1">
                                <p className={`text-sm font-semibold ${
                                  isActive ? config.text : isDone ? config.text : "text-gray-500"
                                }`}>
                                  {step.label}
                                </p>
                                {isDone && (
                                  <CheckCircle2 size={16} className={`${config.text}`} />
                                )}
                                {isActive && (
                                  <motion.span
                                    animate={{ opacity: [1, 0.5, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className={`text-xs font-medium ${config.text}`}
                                  >
                                    Active
                                  </motion.span>
                                )}
                              </div>
                              <p className={`text-xs ${
                                isActive ? "text-gray-600" : "text-gray-500"
                              }`}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ClaimProgressResponsive;
